/**
 * The one card every notification in the app is rendered in.
 *
 * Toasts used to be whatever string the call site wrote, centred in a plain pill: no icon,
 * no hierarchy, no way to tell progress from success from failure at a glance. A sync
 * finishing and a device failing looked identical, and a bare "Revogando acesso (3/10)..."
 * floated with nothing naming what it was.
 *
 * So the SHAPE is fixed here — tone icon, title, optional quieter body — and the call sites
 * only supply words. Callers with nothing but a sentence still get a title, derived from the
 * toast's own type (see `toastTitleFor`), rather than a heading-less fragment.
 */

import React from 'react';
import { CheckCircle2, XCircle, Loader2, Info, AlertTriangle, X } from 'lucide-react';
import { cn } from '../../utils/cn';

export type ToastTone = 'success' | 'error' | 'warning' | 'loading' | 'info';

export interface ToastCardProps {
  tone: ToastTone;
  /** Lead line. Omit when passing `children` — a component message brings its own. */
  title?: React.ReactNode;
  /** Supporting detail, one step quieter. */
  body?: React.ReactNode;
  /** A complete message element (e.g. DeviceFailureToast) rendered in place of title/body. */
  children?: React.ReactNode;
  onDismiss?: () => void;
  isDark?: boolean;
  /** react-hot-toast's enter/leave flag, so dismissal fades instead of snapping. */
  visible?: boolean;
}

const TONE_ICON: Record<ToastTone, { Icon: typeof Info; className: string; wrap: string }> = {
  success: { Icon: CheckCircle2, className: 'text-green-600', wrap: 'bg-green-500/10' },
  error: { Icon: XCircle, className: 'text-red-500', wrap: 'bg-red-500/10' },
  warning: { Icon: AlertTriangle, className: 'text-amber-500', wrap: 'bg-amber-500/10' },
  loading: { Icon: Loader2, className: 'text-blue-500 animate-spin', wrap: 'bg-blue-500/10' },
  info: { Icon: Info, className: 'text-gray-500', wrap: 'bg-gray-500/10' },
};

/** Default title for a caller that only wrote a sentence. */
export function toastTitleFor(type: string): string {
  switch (type) {
    case 'success': return 'Concluído';
    case 'error': return 'Falha';
    case 'loading': return 'Em andamento';
    default: return 'Aviso';
  }
}

/** react-hot-toast's type vocabulary, in the card's. */
export function toastToneFor(type: string): ToastTone {
  switch (type) {
    case 'success': return 'success';
    case 'error': return 'error';
    case 'loading': return 'loading';
    default: return 'info';
  }
}

export function ToastCard({
  tone,
  title,
  body,
  children,
  onDismiss,
  isDark = false,
  visible = true,
}: ToastCardProps) {
  const { Icon, className, wrap } = TONE_ICON[tone];
  // Work still running must not offer a close button: dismissing it reads as cancelling it.
  const canDismiss = !!onDismiss && tone !== 'loading';

  return (
    <div
      className={cn(
        'w-[360px] max-w-[92vw] rounded-xl border shadow-lg px-3 py-2.5',
        'flex items-start gap-2.5 transition-opacity duration-200',
        isDark ? 'bg-gray-900 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900',
        visible ? 'opacity-100' : 'opacity-0',
      )}
    >
      <span
        data-testid={`toast-icon-${tone}`}
        className={cn('mt-0.5 flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center', wrap)}
      >
        <Icon className={cn('w-3.5 h-3.5', className)} />
      </span>

      <div className="min-w-0 flex-1">
        {children ?? (
          <>
            {/* line-clamp-2: a title is a label. Three bold lines is a banner, which is what
                a sentence passed as a title used to produce. */}
            <p data-testid="toast-title" className="text-sm font-semibold leading-snug break-words line-clamp-2">
              {title}
            </p>
            {body && (
              <p className={cn('text-xs leading-snug break-words mt-0.5', isDark ? 'text-gray-400' : 'text-gray-500')}>
                {body}
              </p>
            )}
          </>
        )}
      </div>

      {canDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Fechar"
          className={cn(
            'flex-shrink-0 p-1 -mr-1 -mt-0.5 rounded-md transition-colors',
            isDark ? 'text-gray-500 hover:bg-gray-800' : 'text-gray-400 hover:bg-gray-100',
          )}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
